import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser, isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useNavigate} from 'react-router';

type WorkstreamUpdateDueDate = {
  id: number;
  name: string;
  next_due_date: string | null;
};

type WorkstreamUpdatesResponse = {
  workstreams: WorkstreamUpdateDueDate[];
};

const fetchWorkstreamUpdates = async (): Promise<WorkstreamUpdatesResponse> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/api/v1/workstream/admin/updates',
  }) as Promise<WorkstreamUpdatesResponse>;

const compareString = (left: string, right: string) => left.localeCompare(right);

const getSortableDueDate = (value?: string | null) => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

const formatDueDate = (value?: string | null) => value ?? 'None';

const columns: Column<WorkstreamUpdateDueDate>[] = [
  {
    type: 'Workstream',
    fixed: true,
    minWidth: 360,
    sort: (a, b) => compareString(a.name, b.name),
  },
  {
    type: 'get_next_due_date(workstream)',
    minWidth: 280,
    sort: (a, b) => getSortableDueDate(a.next_due_date) - getSortableDueDate(b.next_due_date),
  },
];

const WorkstreamRow = ({workstream}: {workstream: WorkstreamUpdateDueDate}) => (
  <Table.Row isSelectable>
    <Table.Cell>
      <Link
        to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(workstream.id))}
        monochromatic
        hasNoUnderline
        showUnderlineOnHover
      >
        <Text>{workstream.name}</Text>
      </Link>
    </Table.Cell>
    <Table.Cell>
      <Text color={workstream.next_due_date ? 'standard' : 'subtle'}>
        {formatDueDate(workstream.next_due_date)}
      </Text>
    </Table.Cell>
  </Table.Row>
);

export const AdminWorkstreamUpdates = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  useDocumentTitle('Workstream updates');

  const {data, isError, isLoading} = useQuery({
    queryKey: ['admin', 'workstream-updates'],
    queryFn: fetchWorkstreamUpdates,
  });

  const workstreams = data?.workstreams ?? [];
  const workstreamCount = workstreams.length;

  if (!pulseUser) {
    return null;
  }

  if (!isUserAdmin(pulseUser) && !isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Workstream updates', to: ROUTE_PATHS.ADMIN_WORKSTREAM_UPDATES},
      ]}
    >
      <Stack gap="24">
        <Stack gap="8">
          <Title size={24}>Workstream updates</Title>
          {data ? (
            <Text color="subtle">
              {workstreamCount} workstream{workstreamCount === 1 ? '' : 's'}
            </Text>
          ) : null}
        </Stack>

        <Table
          columns={columns}
          data={workstreams}
          sortConfig={{columnId: 'Workstream', asc: true}}
          compact
        >
          <Table.Header />
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box display="flex" alignItems="center" style={{gap: 8}}>
                    <Spinner size="small" />
                    <Text color="subtle">Loading workstreams</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : isError ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Text color="subtle">Unable to load workstreams.</Text>
                </Table.Cell>
              </Table.Row>
            ) : workstreamCount === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Text color="subtle">No workstreams found.</Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              <Table.Rows renderRow={(workstream) => <WorkstreamRow workstream={workstream} />} />
            )}
          </Table.Body>
        </Table>
      </Stack>
    </Layout.Container>
  );
};
